import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { cn, formatCurrency } from "@/lib/utils";
import { toast } from "sonner";
import EmptyState from "@/components/common/EmptyState";
import {
  Search,
  ChevronLeft,
  ChevronRight,
  Eye,
  MoreVertical,
  Loader2,
  FileText,
  AlertTriangle,
  CheckCircle2,
  Clock3,
  CircleAlert,
  TrendingUp,
} from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";

const INVOICES_CACHE_KEY_PREFIX = "sales_invoices_cache_v1";
const INVOICES_CACHE_TTL_MS = 10 * 60 * 1000;
const ACCOUNTING_PROVIDER_CACHE_KEY = "obihub_accounting_provider_last";
const FETCH_PAGE_DELAY_MS = 180;
const RETRY_BASE_DELAY_MS = 600;

function getCachedAccountingProvider() {
  try {
    const raw = String(sessionStorage.getItem(ACCOUNTING_PROVIDER_CACHE_KEY) || "").trim();
    return raw === "parasut" || raw === "bizimhesap" ? raw : "";
  } catch {
    return "";
  }
}

function cacheAccountingProvider(provider) {
  try {
    if (provider === "parasut" || provider === "bizimhesap") {
      sessionStorage.setItem(ACCOUNTING_PROVIDER_CACHE_KEY, provider);
    }
  } catch {
    // ignore cache errors
  }
}

function resolveActiveProvider(cfg) {
  const preferred = String(cfg?.provider || "").toLowerCase();
  const parasutConnected = Boolean(cfg?.parasut?.connected);
  const bizimhesapConnected = Boolean(cfg?.bizimhesap?.connected);
  if (preferred === "bizimhesap" && bizimhesapConnected) return "bizimhesap";
  if (preferred === "parasut" && parasutConnected) return "parasut";
  if (bizimhesapConnected) return "bizimhesap";
  if (parasutConnected) return "parasut";
  return "";
}

function formatDate(dateStr) {
  if (!dateStr) return "-";
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  const months = ["Ocak","Şubat","Mart","Nisan","Mayıs","Haziran","Temmuz","Ağustos","Eylül","Ekim","Kasım","Aralık"];
  return `${String(d.getDate()).padStart(2, "0")} ${months[d.getMonth()]}`;
}

function formatDateFull(dateStr) {
  if (!dateStr) return "-";
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  const months = ["Ocak","Şubat","Mart","Nisan","Mayıs","Haziran","Temmuz","Ağustos","Eylül","Ekim","Kasım","Aralık"];
  return `${String(d.getDate()).padStart(2, "0")} ${months[d.getMonth()]} ${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}

function isInPeriod(dateStr, period) {
  if (!dateStr || period === "all") return true;
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return false;
  const now = Date.now();
  const diffMs = now - d.getTime();
  const days = period === "7d" ? 7 : 30;
  return diffMs >= 0 && diffMs <= days * 24 * 60 * 60 * 1000;
}

function matchesInvoiceSearch(inv, q) {
  const needle = String(q || "").trim().toLowerCase();
  if (!needle) return true;
  return [
    inv?.description,
    inv?.invoice_no,
    inv?.order_no,
    inv?.contact_name,
    inv?.platform,
  ]
    .map((v) => String(v || "").toLowerCase())
    .some((v) => v.includes(needle));
}

function getInvoiceStatusMeta(inv) {
  const normalized = String(inv?.status || "").trim().toLowerCase();
  const hasInvoiceNo = Boolean(String(inv?.invoice_no || "").trim());
  if (["cancelled", "canceled", "rejected", "failed", "void", "overdue"].includes(normalized) || normalized.includes("iptal") || normalized.includes("red")) {
    return { key: "cancelled", label: "İptal", cls: "bg-rose-100 text-rose-700 border-rose-200/60", dot: "bg-rose-500" };
  }
  if (hasInvoiceNo || ["paid", "officialized", "finalized", "completed"].includes(normalized) || normalized.includes("resmi")) {
    return { key: "officialized", label: "Resmileştirildi", cls: "bg-emerald-100 text-emerald-800 border-emerald-200/60", dot: "bg-emerald-500" };
  }
  return { key: "draft_sent", label: "Taslak Gönderildi", cls: "bg-amber-100 text-amber-800 border-amber-200/60", dot: "bg-amber-500" };
}

function statusBadge(inv) {
  const meta = getInvoiceStatusMeta(inv);
  return (
    <span className={cn("inline-flex items-center gap-1.5 px-2.5 py-[3px] rounded-full text-xs font-semibold border", meta.cls)}>
      <span className={cn("h-[7px] w-[7px] rounded-full shrink-0", meta.dot)} />
      {meta.label}
    </span>
  );
}

function normalizeProvider(inv) {
  const raw = (inv.platform_provider || inv.platform || "").toString().toLowerCase();
  if (raw.includes("woo")) return "woocommerce";
  if (raw.includes("shopify")) return "shopify";
  if (raw.includes("shopier")) return "shopier";
  if (raw.includes("ikas")) return "ikas";
  if (raw.includes("open")) return "opencart";
  if (raw.includes("trendyol")) return "trendyol";
  if (raw.includes("hepsiburada")) return "hepsiburada";
  if (raw.includes("amazon")) return "amazon";
  if (raw.includes("n11")) return "n11";
  if (raw.includes("ptt")) return "pttavm";
  if (raw.includes("cicek")) return "ciceksepeti";
  return "";
}

function platformLogoSrc(inv) {
  const provider = normalizeProvider(inv);
  return provider ? `/integrations/${provider}.svg` : "";
}

function displayOrderNo(v) {
  const s = (v || "").toString().trim();
  if (!s) return "-";
  const main = s.includes(" - ") ? s.split(" - ", 1)[0].trim() : s;
  if (main.includes("_")) {
    const left = main.split("_", 1)[0].trim();
    if (/^\d+$/.test(left)) return left;
  }
  return main;
}

export default function InvoicesPage() {
  const navigate = useNavigate();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [allInvoices, setAllInvoices] = useState([]);
  const [accountingProvider, setAccountingProvider] = useState("");
  const [period, setPeriod] = useState("30d");
  const requestSeqRef = useRef(0);
  const perPage = 15;

  useEffect(() => {
    const t = setTimeout(() => {
      const q = search.trim();
      if (!q || q.length >= 2) setDebouncedSearch(q);
    }, 350);
    return () => clearTimeout(t);
  }, [search]);

  const readInvoicesCache = useCallback((provider) => {
    try {
      const raw = sessionStorage.getItem(`${INVOICES_CACHE_KEY_PREFIX}_${provider || "unknown"}`);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (!parsed?.ts || !Array.isArray(parsed?.rows)) return null;
      if (Date.now() - Number(parsed.ts) > INVOICES_CACHE_TTL_MS) return null;
      return parsed.rows;
    } catch {
      return null;
    }
  }, []);

  const writeInvoicesCache = useCallback((provider, rows) => {
    try {
      sessionStorage.setItem(`${INVOICES_CACHE_KEY_PREFIX}_${provider || "unknown"}`, JSON.stringify({ ts: Date.now(), rows }));
    } catch {
      // ignore cache errors
    }
  }, []);

  const fetchAllInvoices = useCallback(async (provider) => {
    const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
    const requestWithRetry = async (url, maxRetries = 3) => {
      let attempt = 0;
      while (attempt <= maxRetries) {
        try {
          return await api.get(url);
        } catch (err) {
          const message = String(err?.message || "");
          const is429 = message.includes("429");
          if (!is429 || attempt === maxRetries) throw err;
          await sleep(RETRY_BASE_DELAY_MS * (attempt + 1));
          attempt += 1;
        }
      }
      return null;
    };

    const base = provider === "bizimhesap" ? "/bizimhesap/invoices" : "/parasut/invoices";
    const widePerPage = 25;
    const first = await requestWithRetry(`${base}?page=1&per_page=${widePerPage}`);
    const firstRows = first?.invoices || [];
    const firstTotal = Number(first?.total || firstRows.length || 0);
    const totalPagesWide = Math.max(1, Math.ceil(firstTotal / widePerPage));
    const allRows = [...firstRows];
    for (let p = 2; p <= totalPagesWide; p += 1) {
      await sleep(FETCH_PAGE_DELAY_MS);
      const next = await requestWithRetry(`${base}?page=${p}&per_page=${widePerPage}`);
      allRows.push(...(next?.invoices || []));
    }
    return allRows;
  }, []);

  const fetchInvoices = useCallback(async () => {
    setLoading(true);
    setError(null);
    const reqId = ++requestSeqRef.current;
    try {
      const cfg = await api.get("/settings/accounting").catch(() => null);
      const provider = resolveActiveProvider(cfg) || getCachedAccountingProvider() || String(accountingProvider || "");
      if (reqId !== requestSeqRef.current) return;
      setAccountingProvider(provider);
      if (provider) cacheAccountingProvider(provider);
      if (!provider) {
        setError("accounting_not_connected");
        setInvoices([]);
        setTotal(0);
        return;
      }

      const base = provider === "bizimhesap" ? "/bizimhesap/invoices" : "/parasut/invoices";
      const q = debouncedSearch.trim().toLowerCase();
      if (!q) {
        const data = await api.get(`${base}?page=${page}&per_page=${perPage}`);
        if (reqId !== requestSeqRef.current) return;
        setInvoices(data?.invoices || []);
        setTotal(Number(data?.total || 0));
        return;
      }

      let source = allInvoices;
      if (provider !== accountingProvider && source.length) {
        source = [];
        setAllInvoices([]);
      }
      if (!source.length) {
        const cached = readInvoicesCache(provider);
        if (cached?.length) {
          source = cached;
        } else {
          source = await fetchAllInvoices(provider);
          writeInvoicesCache(provider, source);
        }
        if (reqId !== requestSeqRef.current) return;
        setAllInvoices(source);
      }

      const filteredAll = source.filter((inv) => matchesInvoiceSearch(inv, q));
      const start = (page - 1) * perPage;
      const end = start + perPage;
      if (reqId !== requestSeqRef.current) return;
      setInvoices(filteredAll.slice(start, end));
      setTotal(filteredAll.length);
    } catch {
      setError("generic");
      toast.error("Faturalar yüklenirken hata oluştu");
    } finally {
      if (reqId === requestSeqRef.current) setLoading(false);
    }
  }, [accountingProvider, allInvoices, debouncedSearch, fetchAllInvoices, page, readInvoicesCache, writeInvoicesCache]);

  useEffect(() => {
    fetchInvoices();
  }, [fetchInvoices]);

  const totalPages = Math.ceil(total / perPage) || 1;
  const filteredInvoices = invoices;
  const dashboardStats = useMemo(() => {
    const sourcePool = allInvoices.length ? allInvoices : filteredInvoices;
    const searched = sourcePool.filter((item) => matchesInvoiceSearch(item, debouncedSearch));
    const source = searched.filter((item) => isInPeriod(item?.issue_date, period));
    const grossTotal = source.reduce((acc, item) => acc + Number(item.display_gross_total ?? item.gross_total ?? 0), 0);
    const officialized = source.filter((item) => getInvoiceStatusMeta(item).key === "officialized").length;
    const draftSent = source.filter((item) => getInvoiceStatusMeta(item).key === "draft_sent").length;
    const cancelled = source.filter((item) => getInvoiceStatusMeta(item).key === "cancelled").length;
    return { grossTotal, officialized, draftSent, cancelled };
  }, [allInvoices, debouncedSearch, filteredInvoices, period]);

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    if (selectedIds.size === filteredInvoices.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredInvoices.map((i) => i.id)));
    }
  };

  const openInvoiceDetail = (invoiceId) => {
    if (accountingProvider !== "parasut") {
      toast.info("Bu muhasebe sağlayıcısı için fatura detay ekranı yakında aktif olacak.");
      return;
    }
    navigate(`/panel/satislar/faturalar/${invoiceId}`);
  };

  if (error === "accounting_not_connected") {
    return (
      <div className="h-[calc(100dvh-3.5rem)] flex items-center justify-center p-6">
        <div className="text-center max-w-md">
          <div className="mx-auto h-16 w-16 rounded-2xl bg-amber-50 border border-amber-200 grid place-items-center mb-4">
            <AlertTriangle className="h-8 w-8 text-amber-500" />
          </div>
          <h2 className="text-lg font-semibold text-foreground mb-2">Muhasebe Bağlantısı Gerekli</h2>
          <p className="text-sm text-muted-foreground mb-4">Faturaları görüntülemek için aktif bir muhasebe entegrasyonu bağlamanız gerekir.</p>
          <button
            onClick={() => navigate("/panel/hesabim/firmalarim")}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-[#1487a3] text-white text-sm font-medium hover:bg-[#1e7ab3] transition-colors"
          >
            Firmalarıma Git
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="page-shell min-h-[calc(100dvh-3.5rem)] overflow-x-hidden" data-testid="invoices-page">
      <div className="surface-card overflow-hidden">
        <div className="sheet-heading flex-wrap gap-y-3">
          <div>
            <h2 data-testid="invoices-title">Faturalar</h2>
            {!loading && <p>{total} fatura</p>}
          </div>
          <div className="sheet-heading-actions">
            <div className="flex items-center gap-3 rounded-lg border bg-background px-3 h-10 w-full sm:w-72">
              <Search className="h-4 w-4 text-muted-foreground shrink-0" />
              <input
                type="text"
                value={search}
                onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                placeholder="Fatura ara..."
                className="flex-1 bg-transparent text-sm outline-none"
                data-testid="invoices-search"
              />
            </div>
          </div>
        </div>

        {!loading && filteredInvoices.length > 0 && (
          <div className="border-b border-border bg-card/60 p-3">
            <div className="mb-2 flex items-center justify-end gap-1">
              <Button variant={period === "7d" ? "default" : "outline"} size="sm" onClick={() => setPeriod("7d")} className="h-8 px-3 text-xs">7 Gün</Button>
              <Button variant={period === "30d" ? "default" : "outline"} size="sm" onClick={() => setPeriod("30d")} className="h-8 px-3 text-xs">30 Gün</Button>
              <Button variant={period === "all" ? "default" : "outline"} size="sm" onClick={() => setPeriod("all")} className="h-8 px-3 text-xs">Tümü</Button>
            </div>
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-4">
            <div className="rounded-xl border border-emerald-200/70 bg-gradient-to-br from-emerald-50 to-white p-3 shadow-[0_8px_24px_rgba(16,185,129,0.08)]">
              <div className="flex items-center justify-between">
                <span className="text-xs font-medium text-emerald-700">Resmileşen</span>
                <CheckCircle2 className="h-4 w-4 text-emerald-500" />
              </div>
              <div className="mt-2 text-xl font-semibold tabular-nums text-emerald-900">{dashboardStats.officialized}</div>
            </div>
            <div className="rounded-xl border border-amber-200/70 bg-gradient-to-br from-amber-50 to-white p-3 shadow-[0_8px_24px_rgba(245,158,11,0.08)]">
              <div className="flex items-center justify-between">
                <span className="text-xs font-medium text-amber-700">Taslak Gönderildi</span>
                <Clock3 className="h-4 w-4 text-amber-500" />
              </div>
              <div className="mt-2 text-xl font-semibold tabular-nums text-amber-900">{dashboardStats.draftSent}</div>
            </div>
            <div className="rounded-xl border border-rose-200/70 bg-gradient-to-br from-rose-50 to-white p-3 shadow-[0_8px_24px_rgba(244,63,94,0.08)]">
              <div className="flex items-center justify-between">
                <span className="text-xs font-medium text-rose-700">İptal</span>
                <CircleAlert className="h-4 w-4 text-rose-500" />
              </div>
              <div className="mt-2 text-xl font-semibold tabular-nums text-rose-900">{dashboardStats.cancelled}</div>
            </div>
            <div className="rounded-xl border border-sky-200/70 bg-gradient-to-br from-sky-50 to-white p-3 shadow-[0_8px_24px_rgba(14,165,233,0.08)]">
              <div className="flex items-center justify-between">
                <span className="text-xs font-medium text-sky-700">Toplam Tutar</span>
                <TrendingUp className="h-4 w-4 text-sky-500" />
              </div>
              <div className="mt-2 text-base font-semibold tabular-nums text-sky-900">{formatCurrency(dashboardStats.grossTotal)}</div>
            </div>
          </div>
          </div>
        )}

        <div className="flex-1 overflow-auto bg-card/70">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              <span className="ml-2 text-sm text-muted-foreground">Faturalar yükleniyor...</span>
            </div>
          ) : filteredInvoices.length === 0 ? (
            invoices.length === 0 ? (
              <EmptyState
                icon={<FileText strokeWidth={1.5} />}
                title="Henüz fatura yok"
                desc="Siparişlerinizden fatura oluşturabilir veya Paraşüt/BizimHesap entegrasyonunuzu bağlayabilirsiniz."
                action={{ label: "Entegrasyonlara Git", onClick: () => window.location.href = "/panel/entegrasyonlar" }}
              />
            ) : (
              <EmptyState
                icon={<Search strokeWidth={1.5} />}
                title="Fatura bulunamadı"
                desc="Arama veya filtre kriterlerinizi değiştirin."
                size="sm"
              />
            )
          ) : (
            <>
              <div className="md:hidden space-y-2 p-3">
                {filteredInvoices.map((inv) => (
                  <button
                    key={`card-${inv.id}`}
                    type="button"
                    onClick={() => openInvoiceDetail(inv.id)}
                    className="w-full rounded-xl border border-border bg-card p-3 text-left"
                  >
                    <div className="flex items-start gap-3">
                      <div className="relative top-0.5 h-10 w-10 rounded-lg border border-border bg-card grid place-items-center overflow-hidden shadow-sm shrink-0">
                        {platformLogoSrc(inv) ? (
                          <img src={platformLogoSrc(inv)} alt={inv.platform || "platform"} className="h-6 w-6 object-contain" />
                        ) : (
                          <span className="text-[10px] text-muted-foreground font-semibold">{(inv.platform || "PL").slice(0, 2).toUpperCase()}</span>
                        )}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="font-medium text-foreground truncate">{inv.description || `Fatura #${inv.id}`}</div>
                        <div className="text-[11px] text-muted-foreground truncate mt-0.5">{inv.contact_name || "-"}</div>
                        <div className="mt-2 flex items-center justify-between text-xs text-muted-foreground">
                          <span>{displayOrderNo(inv.order_no)}</span>
                          <span>{formatDate(inv.issue_date)}</span>
                        </div>
                        <div className="mt-2 flex items-center justify-between">
                          <div>{statusBadge(inv)}</div>
                          <div className="text-sm font-semibold text-foreground">{formatCurrency(Number(inv.display_gross_total ?? inv.gross_total ?? 0))}</div>
                        </div>
                      </div>
                    </div>
                  </button>
                ))}
              </div>

              <table className="hidden md:table w-full table-fixed text-sm" data-testid="invoices-table">
                <colgroup>
                  <col style={{ width: "38px" }} />
                  <col style={{ width: "64px" }} />
                  <col style={{ width: "210px" }} />
                  <col style={{ width: "72px" }} />
                  <col style={{ width: "120px" }} />
                  <col style={{ width: "110px" }} />
                  <col style={{ width: "136px" }} />
                  <col style={{ width: "90px" }} />
                  <col style={{ width: "100px" }} />
                  <col style={{ width: "120px" }} />
                  <col style={{ width: "66px" }} />
                </colgroup>
                <thead className="sticky top-0 z-10 border-b border-border bg-muted/30">
                  <tr>
                    <th className="w-10 px-2 py-3 text-left">
                      <Checkbox
                        checked={selectedIds.size === filteredInvoices.length && filteredInvoices.length > 0}
                        onCheckedChange={toggleAll}
                      />
                    </th>
                    <th className="px-1.5 py-3 text-left text-[10.5px] font-bold uppercase tracking-[0.07em] text-muted-foreground whitespace-nowrap">Platform</th>
                    <th className="px-2 py-3 text-left text-[10.5px] font-bold uppercase tracking-[0.07em] text-muted-foreground whitespace-nowrap">Fatura Açıklaması</th>
                    <th className="px-2 py-3 text-left text-[10.5px] font-bold uppercase tracking-[0.07em] text-muted-foreground whitespace-nowrap">Fatura Tipi</th>
                    <th className="px-2 py-3 text-left text-[10.5px] font-bold uppercase tracking-[0.07em] text-muted-foreground whitespace-nowrap">Sipariş No</th>
                    <th className="px-2 py-3 text-center text-[10.5px] font-bold uppercase tracking-[0.07em] text-muted-foreground whitespace-nowrap">Sipariş Tarihi</th>
                    <th className="px-2 py-3 text-left text-[10.5px] font-bold uppercase tracking-[0.07em] text-muted-foreground whitespace-nowrap">Fatura No</th>
                    <th className="px-2 py-3 text-center text-[10.5px] font-bold uppercase tracking-[0.07em] text-muted-foreground whitespace-nowrap">Fatura Tarihi</th>
                    <th className="px-2 py-3 text-left text-[10.5px] font-bold uppercase tracking-[0.07em] text-muted-foreground whitespace-nowrap">Durum</th>
                    <th className="px-2 py-3 text-right text-[10.5px] font-bold uppercase tracking-[0.07em] text-muted-foreground whitespace-nowrap">Genel Toplam</th>
                    <th className="w-20 px-3 py-3" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {filteredInvoices.map((inv) => (
                    <tr key={inv.id} className={cn("hover:bg-muted/50/80 transition-colors", selectedIds.has(inv.id) && "bg-primary/5")}>
                      <td className="px-2 py-3">
                        <Checkbox checked={selectedIds.has(inv.id)} onCheckedChange={() => toggleSelect(inv.id)} />
                      </td>
                      <td className="px-0.5 py-3 align-middle">
                        <div className="flex h-full items-center justify-start">
                          <div className="h-11 w-11 rounded-xl border border-border/60 bg-card grid place-items-center overflow-hidden shadow-sm">
                            {platformLogoSrc(inv) ? (
                              <img src={platformLogoSrc(inv)} alt={inv.platform || "platform"} className="h-6 w-6 object-contain" loading="lazy" />
                            ) : (
                              <span className="text-[10px] text-muted-foreground font-bold">{(inv.platform || "PL").slice(0, 2).toUpperCase()}</span>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-2 py-3">
                        <button onClick={() => openInvoiceDetail(inv.id)} className="text-left group">
                          <div className="font-semibold text-[13px] text-foreground group-hover:text-[#1487a3] transition-colors truncate max-w-[240px]" title={inv.description}>
                            {inv.description || `Fatura #${inv.id}`}
                          </div>
                          <div className="mt-0.5 text-[11px] text-muted-foreground truncate max-w-[240px]" title={inv.contact_name || "-"}>{inv.contact_name || "-"}</div>
                        </button>
                      </td>
                      <td className="px-2 py-3"><span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-transparent text-muted-foreground">Satış</span></td>
                      <td className="px-2 py-3 text-foreground whitespace-nowrap">{displayOrderNo(inv.order_no)}</td>
                      <td className="px-2 py-3 text-center text-muted-foreground whitespace-nowrap">{formatDateFull(inv.order_date)}</td>
                      <td className="px-2 py-3">
                        <button onClick={() => openInvoiceDetail(inv.id)} className="text-foreground hover:text-[#1487a3] font-medium transition-colors">
                          {inv.invoice_no || "-"}
                        </button>
                      </td>
                      <td className="px-2 py-3 text-center text-muted-foreground whitespace-nowrap">{formatDate(inv.issue_date)}</td>
                      <td className="px-2 py-3">{statusBadge(inv)}</td>
                      <td className="px-2 py-3 text-right font-medium text-foreground whitespace-nowrap">{formatCurrency(Number(inv.display_gross_total ?? inv.gross_total ?? 0))}</td>
                      <td className="px-3 py-3">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            onClick={() => openInvoiceDetail(inv.id)}
                            className="h-8 w-8 rounded-lg inline-flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                            title="Görüntüle"
                          >
                            <Eye className="h-4 w-4" />
                          </button>
                          <button className="h-8 w-8 rounded-lg inline-flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted transition-colors" title="Diğer">
                            <MoreVertical className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
        </div>

        {!loading && filteredInvoices.length > 0 && (
          <div className="flex-none border-t border-border bg-card px-3 sm:px-4 md:px-6 py-3 flex items-center justify-between gap-3" data-testid="invoices-pagination">
            <span className="text-xs text-muted-foreground">
              {total} faturadan {(page - 1) * perPage + 1}-{Math.min(page * perPage, total)} arası gösteriliyor
            </span>
            <div className="flex items-center gap-1">
              <Button variant="outline" size="icon" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))} data-testid="invoices-prev-page">
                <ChevronLeft className="h-4 w-4" />
              </Button>
              {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                let p;
                if (totalPages <= 5) p = i + 1;
                else if (page <= 3) p = i + 1;
                else if (page >= totalPages - 2) p = totalPages - 4 + i;
                else p = page - 2 + i;
                return (
                  <Button key={p} variant={page === p ? "default" : "outline"} size="icon" onClick={() => setPage(p)} className={cn("h-8 w-8 text-xs font-medium", page !== p && "text-muted-foreground")}>
                    {p}
                  </Button>
                );
              })}
              <Button variant="outline" size="icon" disabled={page >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))} data-testid="invoices-next-page">
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
